import { trendAxis, type LabTrendCombinedSeries } from "@ferrivia/lab-ui";
import type { LabAnalyteCode, LabResult } from "@ferrivia/api-contract";

/* The three analytes the figure draws, in legend order, each pinned to a
   chart-series token. The list is fixed rather than derived from whatever a
   draw happens to carry: which measurements a client is shown over time is a
   content decision, not something the data should silently change draw by
   draw. The full blood count is deliberately not charted — its
   sub-measurements are on file and shown as readings.

   Haemoglobin leads here (Ville, 2026-09-07), which is now a deliberate
   difference from the clinic console's Labs tab — each `seriesIndex` still
   pins the same colour token to the same analyte in both, only the reading
   order changed. */
export const TREND_CODES = [
  { code: "hemoglobin", seriesIndex: 3 },
  { code: "ferritin", seriesIndex: 1 },
  { code: "crp", seriesIndex: 2 },
] as const satisfies ReadonlyArray<{ code: LabAnalyteCode; seriesIndex: 1 | 2 | 3 }>;

// collectedOn is always "YYYY-MM-DD" (selfReportedResultRequestSchema / the
// partner-lab ingest) with no time component — formatted in UTC so it can't
// drift a day off in a browser west of Finland.
export function formatCollectedOn(collectedOn: string, tag: string): string {
  return new Intl.DateTimeFormat(tag, { day: "numeric", month: "numeric", year: "numeric", timeZone: "UTC" }).format(
    new Date(`${collectedOn}T00:00:00Z`),
  );
}

export function formatAxisDate(collectedOn: string, tag: string): string {
  return new Intl.DateTimeFormat(tag, { day: "numeric", month: "short", timeZone: "UTC" }).format(
    new Date(`${collectedOn}T00:00:00Z`),
  );
}

export interface Trend {
  dates: string[];
  series: LabTrendCombinedSeries[];
  axis: ReturnType<typeof trendAxis>;
}

/* Turn the draws into the figure's three series and its one shared scale, or
   null when there is nothing to draw a line between.

   Nothing in here derives anything from a measurement beyond the scale it is
   plotted on: `trendAxis` sees the measured values and nothing else — no
   reference range, which would make the axis an implicit comparison against
   it — and every value it produces is printed as a tick. See "The software
   does not interpret" in the root CLAUDE.md and REQ-MDR-002. */
export function buildTrend(results: LabResult[], labelFor: (code: string) => string): Trend | null {
  // `results` arrives newest first; the chart reads oldest first.
  const draws = [...results].reverse();

  // Two draws taken on the same day are one column on a time axis. Keyed by
  // date with the newer draw last, so the column carries the newer value
  // rather than silently drawing two markers on top of each other.
  const dates = [...new Set(draws.map((draw) => draw.collectedOn))];

  const series: LabTrendCombinedSeries[] = TREND_CODES.flatMap(({ code, seriesIndex }) => {
    const byDate = new Map<string, number>();
    let unit = "";
    let mixedUnits = false;
    for (const draw of draws) {
      const analyte = draw.analytes.find((a) => a.analyteCode === code);
      if (analyte) {
        mixedUnits ||= unit !== "" && unit !== analyte.unit;
        byDate.set(draw.collectedOn, analyte.value);
        unit = analyte.unit;
      }
    }
    // Values in different units share no axis, and we never convert them.
    if (byDate.size === 0 || mixedUnits) {
      return [];
    }
    return [
      {
        analyteCode: code,
        label: labelFor(code),
        unit,
        seriesIndex,
        points: dates.filter((date) => byDate.has(date)).map((date) => ({ collectedOn: date, value: byDate.get(date)! })),
      },
    ];
  });

  const values = series.flatMap((entry) => entry.points.map((point) => point.value));
  // One lab result is one data point, and a point is not a trend: below two
  // draws there is no figure at all — not an empty chart, not a "not enough
  // data yet" placeholder.
  if (dates.length < 2 || series.length === 0) {
    return null;
  }
  return { dates, series, axis: trendAxis(values) };
}
